import { Link, router } from "@inertiajs/react";
import { ArrowLeft, MapPin } from "lucide-react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { FichaBody } from "@/components/ficha/FichaBody";
import { branchMetrics } from "@/components/ficha/metrics";
import type { BookingSummary, Business, PageProps } from "@/types";

interface Props extends PageProps {
  business: Business;
  analytics: BookingSummary;
  can_edit?: boolean;
  can_delete?: boolean;
}

export default function BusinessShow({
  business,
  analytics,
  can_edit = true,
  can_delete = false,
  current_user,
  current_organization,
  current_business,
}: Props) {
  const orgSlug = current_organization?.slug || "";

  const handleDelete = () => {
    if (!confirm(`¿Estás seguro de eliminar "${business.name}"?`)) return;
    router.delete(`/${orgSlug}/businesses/${business.id}`);
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/businesses`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a sucursales
          </Link>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">{business.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {business.address && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {business.address}
                  </span>
                )}
                <span>{business.timezone}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {can_edit && (
                <Link href={`/${orgSlug}/businesses/${business.id}/edit`}>
                  <Button variant="outline">Editar</Button>
                </Link>
              )}
              {can_delete && (
                <Button variant="ghost" className="text-destructive" onClick={handleDelete}>
                  Eliminar
                </Button>
              )}
            </div>
          </div>
        </div>

        <FichaBody
          analytics={analytics}
          timezone={business.timezone || "America/Mexico_City"}
          metrics={branchMetrics(
            analytics,
            business.timezone || "America/Mexico_City",
          )}
          hrefFor={({ period, pastPage }) => {
            const params = new URLSearchParams();
            params.set("period", period || analytics.period);
            if (pastPage && pastPage > 1) params.set("past_page", String(pastPage));
            return `/${orgSlug}/businesses/${business.id}?${params.toString()}`;
          }}
        />
      </div>
    </DashboardLayout>
  );
}
